// v10/src/hooks/useWakeLock.js
//
// Mantiene la pantalla encendida mientras el piloto está haciendo el chequeo.
// - Adquiere el lock cuando `active` pasa a true
// - Libera cuando `active` pasa a false o el componente se desmonta
// - Re-adquiere automáticamente si el usuario vuelve al tab/app (visibilitychange)
// - Silencia errores en browsers que no soportan la API (Safari < 16.4, etc.)

import { useEffect, useRef, useCallback } from 'react';

const useWakeLock = (active) => {
  const lockRef      = useRef(null);
  const activeRef    = useRef(active);
  activeRef.current  = active;

  const acquire = useCallback(async () => {
    // API no disponible (HTTP, browser viejo, etc.)
    if (!('wakeLock' in navigator)) return;
    // Ya tenemos un lock activo
    if (lockRef.current && !lockRef.current.released) return;

    try {
      lockRef.current = await navigator.wakeLock.request('screen');
      lockRef.current.addEventListener('release', () => {
        // El sistema liberó el lock (ej: batería crítica)
        // No hacemos nada — el listener de visibilitychange lo re-adquiere
      });
    } catch (err) {
      // El usuario denegó o el browser no lo permite — ignorar silenciosamente
      if (err.name !== 'NotAllowedError') {
        console.warn('[WakeLock] No se pudo adquirir:', err.message);
      }
    }
  }, []);

  const release = useCallback(async () => {
    if (lockRef.current && !lockRef.current.released) {
      try { await lockRef.current.release(); } catch (_) {}
      lockRef.current = null;
    }
  }, []);

  // Adquirir / liberar según `active`
  useEffect(() => {
    if (active) {
      acquire();
    } else {
      release();
    }
    return () => { release(); };
  }, [active, acquire, release]);

  // Re-adquirir cuando el usuario vuelve a la pestaña / app (Android/iOS)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && activeRef.current) {
        acquire();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [acquire]);
};

export default useWakeLock;
